import { extractPayloadFromHtml } from '../block-selection/blockClipboard';
import {
    detectClipboardSource,
    isTabularData,
    parseClipboardGrid,
} from '../test-table-editor/parseClipboardTable';
import { clipboardHtmlToBlockHtml, plainTextToBlockHtml } from './clipboardToHtml';
import type { BlockClipboardPayload } from '../block-selection/blockClipboard';
import type { ClipboardSource } from '../test-table-editor/parseClipboardTable';

/**
 * The clipboard reduced to the three things the router cares about.
 *
 * Deliberately a plain value rather than a `ClipboardEvent`: the context-menu
 * paste has no event to inspect, it reads `navigator.clipboard` instead, and
 * both paths must route identically.
 */
export interface ClipboardSnapshot {
    html: string;
    text: string;
    imageFile: File | null;
}

export type PasteRoute =
    | { kind: 'blocks'; payload: BlockClipboardPayload }
    | { kind: 'table'; grid: string[][]; source: ClipboardSource }
    | { kind: 'image'; file: File }
    | { kind: 'text'; html: string }
    | { kind: 'none' };

/**
 * Decides what a clipboard payload should become, in a fixed priority order.
 *
 * 1. LaudoPsi blocks. On the keyboard path the capture-phase block handler has
 *    already claimed these, but `navigator.clipboard.read()` strips our custom
 *    MIME type while preserving the base64 carrier in `text/html`, so the menu
 *    path depends on this branch. It also stops a copied-blocks clipboard from
 *    being pasted as a text block holding the "[LaudoPsi] N bloco(s)…"
 *    fingerprint.
 * 2. Tables — checked *before* images on purpose. Copying a range in Excel puts
 *    both a bitmap and TSV/HTML on the clipboard, so an image-first rule would
 *    turn every spreadsheet paste into a screenshot.
 * 3. Images. The empty-text requirement is what separates a real screenshot
 *    from an Excel range or from Word prose containing an inline image.
 * 4. Text, preferring HTML over plain text.
 */
export const classifyPaste = (
    snapshot: ClipboardSnapshot,
    { canInsertImage }: { canInsertImage: boolean },
): PasteRoute => {
    const payload = extractPayloadFromHtml(snapshot.html);
    if (payload) return { kind: 'blocks', payload };

    const parsed = parseClipboardGrid(snapshot.html, snapshot.text);
    if (parsed && isTabularData(parsed)) {
        return {
            kind: 'table',
            grid: parsed.grid,
            source: detectClipboardSource(snapshot.html),
        };
    }

    if (canInsertImage && snapshot.imageFile && snapshot.text.trim() === '') {
        return { kind: 'image', file: snapshot.imageFile };
    }

    const html = clipboardHtmlToBlockHtml(snapshot.html) || plainTextToBlockHtml(snapshot.text);
    if (html) return { kind: 'text', html };

    return { kind: 'none' };
};

/** First image on a `DataTransfer`, checking `items` for browsers that omit `files`. */
const imageFromDataTransfer = (data: DataTransfer | null): File | null => {
    const fromFiles = Array.from(data?.files ?? []).find((file) => file.type.startsWith('image/'));
    if (fromFiles) return fromFiles;

    for (const item of Array.from(data?.items ?? [])) {
        if (item.kind !== 'file' || !item.type.startsWith('image/')) continue;
        const file = item.getAsFile();
        if (file) return file;
    }

    return null;
};

export const snapshotFromEvent = (event: ClipboardEvent): ClipboardSnapshot => ({
    html: event.clipboardData?.getData('text/html') ?? '',
    text: event.clipboardData?.getData('text/plain') ?? '',
    imageFile: imageFromDataTransfer(event.clipboardData),
});
